import { arrayMin, groupBy, isArrayEmpty, selectMany, SoilP2O5Method, sortDates } from "../../omnia"
import { ISoilService } from "../../types"
import { PlanField } from "../types"

/**
 * Describes a group of related standard analyses
 * (i.e., sampled at the same time on the same field)
 */
export interface SelectableStdAnalysis {

    key: string

    date: Date

    description?: string

    selected: {
        value: boolean
        initial: boolean
    }

    field?: StdAnalysisAlloc
    items: StdAnalysisAlloc[]

}

/**
 * Describes a single soil analysis
 */
export interface StdAnalysis {

    key: number

    stdAnalysisId: number

    samplingDate: Date

    /** Analysis number */
    number: string

    /** Field reference */
    reference: string

    allocations: {
        fieldId: number
        /** True if this is a zonal analysis */
        zone: boolean
        /** Zone/plot reference, if applicable */
        reference: string
    }[]

    p2O5?: number
    k2O?: number
    mgO?: number
    morgan: boolean

    pH?: number

    organicMatter?: number


}


/**
 * Represents a soil analysis with a single allocation to a field or zone
 */
export interface StdAnalysisAlloc extends Omit<StdAnalysis, "allocations"> {
    alloc: StdAnalysis["allocations"][0]
}

export function getFieldAnalysisGroups(fieldId: number, analyses: StdAnalysis[], planField: PlanField): SelectableStdAnalysis[] {

    if (!fieldId) {
        return []
    }

    const field = analyses
        .filter(a => a.allocations.some(aa => aa.fieldId === fieldId))
        .map(a => ({
            ...a,
            allocations: a.allocations.filter(aa => aa.fieldId === fieldId)
        }))

    const groups = groupFieldAnalysis(field).sort((a, b) => sortDates(b.date, a.date))

    const result = setSelection(groups, planField)

    return result

}


function setSelection(groups: SelectableStdAnalysis[], { analyses }: PlanField): SelectableStdAnalysis[] {

    if (isArrayEmpty(analyses)) {
        return groups
    }

    const excluded = new Set(analyses.filter(a => (a.type === "std") && (!a.report)).map(a => a.id))

    return groups.map(g => {

        const s = !g.items.every(i => excluded.has(i.key))

        return {
            ...g,
            selected: {
                value: s,
                initial: s
            }
        }

    })

}

function groupFieldAnalysis(a: StdAnalysis[]): SelectableStdAnalysis[] {

    // Unwrap the allocations (they are all for the same field at this stage),
    const alloc = selectMany(
        a.map(a => a.allocations.map(aa => {

            const { allocations, ...analysis } = a

            return {
                ...analysis,
                alloc: aa
            }

        }))
    )

    // Remove duplicates, retaining the first field-level allocation if one exists,
    // any first allocation otherwise
    const unique = groupBy(alloc, a => a.stdAnalysisId)
        .map(g => g.values.find(i => !i.alloc.zone) ?? g.values[0])

    // Sort by sampling date then fields first, zones next
    const sorted = unique
        .sort((a, b) =>
            // Sort by date
            sortDates(a.samplingDate, b.samplingDate)
            // Then by field (first) / zones (next)
            || ((a.alloc.zone ? 1 : 0) - (b.alloc.zone ? 1 : 0))
        )

    // Reduce to create the groups
    // For now, we assume that 2 consecutive records are part of the same group if
    // - Their dates are less than a week apart (this is completely arbitrary)
    // - And they do not represent a zone -> field transition

    const groups = sorted.reduce((groups, r) => {

        const MaxDateDiff = 7 * 24 * 60 * 60 * 1000

        const group = groups[groups.length - 1]
        const last = group ? group[group.length - 1] : undefined

        if (
            // First record
            (!last)
            // Zone -> field transition
            || (last.alloc.zone && (!r.alloc.zone))
            // Date difference
            || ((r.samplingDate.valueOf() - last.samplingDate.valueOf()) > MaxDateDiff)
        ) {
            groups.push([r])
        }
        else {
            group.push(r)
        }

        return groups

    }, [] as StdAnalysisAlloc[][])

    const results = groups.map(createGroupFromAllocations)

    return results

}

function createGroupFromAllocations(allocations: StdAnalysisAlloc[]): SelectableStdAnalysis {

    const field = allocations.find(a => !a.alloc.zone)
    const date = arrayMin(allocations, a => a.samplingDate)
    const analysisId = arrayMin(allocations, a => a.stdAnalysisId)

    const group: SelectableStdAnalysis = {
        key: analysisId.toString(),
        selected: {
            value: true,
            initial: true
        },
        items: allocations,
        date,
        field,
        description: date.toDateString()
    }

    return group

}

export function getStdAnalysisFromSoilService(svc: ISoilService, targetYear: number): StdAnalysis[] {

    const std = svc.getSoilAnalyses(undefined, true)
        .filter(s => s.SamplingDate?.getUTCFullYear() <= targetYear)
        .filter(s => s.Allocations?.some(a => Boolean(a.FieldId)))
        .map(a => {

            const stdAnalysisId = a.SoilAnalysisId
            const key = stdAnalysisId

            const std: StdAnalysis = {

                key,
                stdAnalysisId,

                samplingDate: a.SamplingDate,

                number: a.AnalysisNumber,
                reference: a.FieldReference,

                p2O5: a.P2O5,
                k2O: a.K2O,
                mgO: a.MgO,
                morgan: a.P2O5Method === SoilP2O5Method.Morgan,

                pH: a.WaterpH,
                organicMatter: a.OrganicMatter,

                allocations: a.Allocations.map(aa => ({
                    fieldId: aa.FieldId,
                    zone: aa.ZoneAlloc || Boolean(aa.ZoneId),
                    reference: a.PlotReference
                }))

            }

            return std

        })

    return std
}
